import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '../components/layout/AdminShell';
import { FieldHelp, FieldHelpGroup } from '../components/FieldHelp';
import { Modal } from '../components/Modal';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/ToastHost';
import { clientsHelp } from '../lib/fieldHelpCopy';
import {
  getCoreUser,
  listClients,
  onboardClient,
  resetPartnerPassword,
  syncClientMaster,
  updateClient,
  type CoreUserRecord,
} from '../lib/adminApi';
import type { ClientRecord } from '../types/registry';
import { can, useStaffMe } from '../lib/permissions';

function isBlank(value: string | undefined): boolean {
  return !value || !value.trim();
}

export function ClientsPage(): JSX.Element {
  const meQuery = useStaffMe();
  const me = meQuery.data;
  const canCreate = can(me, 'clients', 'create');
  const canEdit = can(me, 'clients', 'edit');
  const queryClient = useQueryClient();
  const { success } = useToast();
  const listQuery = useQuery({ queryKey: ['clients'], queryFn: listClients });
  const [createOpen, setCreateOpen] = useState(false);
  const [editClient, setEditClient] = useState<ClientRecord | null>(null);
  const [userInfo, setUserInfo] = useState<CoreUserRecord | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [clientType, setClientType] = useState('organization');
  const [address, setAddress] = useState('');
  const [nit, setNit] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [masterId, setMasterId] = useState('');
  const [masterEmail, setMasterEmail] = useState('');
  const [status, setStatus] = useState('active');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      onboardClient({
        display_name: displayName,
        client_type: clientType,
        master_email: masterEmail.trim(),
        address,
        nit,
        city,
        country,
      }),
    onSuccess: async (created) => {
      const name = displayName;
      resetCreateForm();
      setCreateOpen(false);
      success(`Client "${name}" onboarded (${created.client_id ?? 'ok'}) — setup email sent`);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!editClient) throw new Error('No client selected');
      const body: Partial<ClientRecord> = {};
      if (displayName.trim() && displayName.trim() !== editClient.display_name) {
        body.display_name = displayName.trim();
      }
      if (status !== editClient.status) body.status = status;
      if (isBlank(editClient.address) && address.trim()) body.address = address.trim();
      if (isBlank(editClient.nit) && nit.trim()) body.nit = nit.trim();
      if (isBlank(editClient.city) && city.trim()) body.city = city.trim();
      if (isBlank(editClient.country) && country.trim()) body.country = country.trim();
      if (isBlank(editClient.master_id) && masterId.trim()) body.master_id = masterId.trim();
      return updateClient(editClient.client_id, body);
    },
    onSuccess: async (updated) => {
      setEditClient(null);
      success(`Client "${updated.display_name}" updated`);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const syncMutation = useMutation({
    mutationFn: () => {
      if (!editClient) throw new Error('No client selected');
      return syncClientMaster(editClient.client_id);
    },
    onSuccess: async (result) => {
      setMasterId(result.master_id);
      setEditClient((prev) =>
        prev ? { ...prev, master_id: result.master_id } : prev,
      );
      setError(null);
      success(`Master user synced for ${editClient?.display_name ?? 'client'}`);
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const passwordResetMutation = useMutation({
    mutationFn: () => {
      if (!editClient) throw new Error('No client selected');
      return resetPartnerPassword(editClient.client_id);
    },
    onSuccess: () => {
      setError(null);
      success('Partner password reset email sent');
    },
    onError: (err: Error) => setError(err.message),
  });

  const userInfoMutation = useMutation({
    mutationFn: (userId: string) => getCoreUser(userId),
    onSuccess: (user) => {
      setUserInfo(user);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  function resetCreateForm() {
    setDisplayName('');
    setClientType('organization');
    setAddress('');
    setNit('');
    setCity('');
    setCountry('');
    setMasterId('');
    setMasterEmail('');
    setStatus('active');
    setError(null);
  }

  function openEdit(client: ClientRecord) {
    setError(null);
    setUserInfo(null);
    setEditClient(client);
    setDisplayName(client.display_name);
    setStatus(client.status || 'active');
    setAddress(client.address ?? '');
    setNit(client.nit ?? '');
    setCity(client.city ?? '');
    setCountry(client.country ?? '');
    setMasterId(client.master_id ?? '');
  }

  function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  function handleEdit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    editMutation.mutate();
  }

  const masterIdLocked = Boolean(editClient && !isBlank(editClient.master_id));

  return (
    <AdminShell title="Clients" subtitle="Portal clients registry">
      <div className="panel">
        <div className="panel-toolbar">
          <p className="panel-toolbar__meta">Portal global clients</p>
          <div className="panel-toolbar__actions">
            {canCreate ? (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  resetCreateForm();
                  setCreateOpen(true);
                }}
              >
                Create client
              </button>
            ) : null}
          </div>
        </div>
        {listQuery.isLoading ? <p>Loading…</p> : null}
        {listQuery.isError ? (
          <div className="form-error">
            {listQuery.error instanceof Error ? listQuery.error.message : 'Failed to load clients'}
          </div>
        ) : null}
        {error && !createOpen && !editClient && !userInfo ? (
          <div className="form-error">{error}</div>
        ) : null}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Display name</th>
                <th>Client ID</th>
                <th>Type</th>
                <th>City</th>
                <th>NIT</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(listQuery.data ?? []).length === 0 && !listQuery.isLoading && !listQuery.isError ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <p className="empty-state__title">No clients yet</p>
                      <p className="empty-state__copy">
                        Create the first Portal client to register organizations.
                      </p>
                      {canCreate ? (
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => {
                            resetCreateForm();
                            setCreateOpen(true);
                          }}
                        >
                          Create client
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : null}
              {(listQuery.data ?? []).map((client) => (
                <tr key={client.client_id}>
                  <td>{client.display_name}</td>
                  <td>
                    <code>{client.client_id}</code>
                  </td>
                  <td>{client.client_type}</td>
                  <td>{client.city || '—'}</td>
                  <td>{client.nit || '—'}</td>
                  <td>
                    <StatusPill status={client.status} />
                  </td>
                  <td className="table-actions">
                    {canEdit ? (
                      <button type="button" className="ghost-button" onClick={() => openEdit(client)}>
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && canCreate ? (
        <Modal title="Onboard client" onClose={() => setCreateOpen(false)} closeOnBackdrop={false}>
          <p className="modal-card__copy">
            Creates Cognito master + Portal client + Core roles via Identity. Organization and
            individual masters get a provisional password email; partners get a setup link on
            login.sense-ai.co.
          </p>
          <form className="login-form" onSubmit={handleCreate}>
            <FieldHelpGroup>
              <FieldHelp label="Display name" help={clientsHelp.displayName}>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
              </FieldHelp>
              <FieldHelp label="Type" help={clientsHelp.clientType}>
                <select value={clientType} onChange={(e) => setClientType(e.target.value)}>
                  <option value="organization">organization</option>
                  <option value="individual">individual</option>
                  <option value="partner">partner</option>
                </select>
              </FieldHelp>
              <FieldHelp label="Master email" help="Email for the client master invite / setup link">
                <input
                  type="email"
                  value={masterEmail}
                  onChange={(e) => setMasterEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </FieldHelp>
              <FieldHelp label="Address" help={clientsHelp.address}>
                <input value={address} onChange={(e) => setAddress(e.target.value)} required />
              </FieldHelp>
              <FieldHelp label="NIT" help={clientsHelp.nit}>
                <input value={nit} onChange={(e) => setNit(e.target.value)} required />
              </FieldHelp>
              <FieldHelp label="City" help={clientsHelp.city}>
                <input value={city} onChange={(e) => setCity(e.target.value)} required />
              </FieldHelp>
              <FieldHelp label="Country" help={clientsHelp.country}>
                <input value={country} onChange={(e) => setCountry(e.target.value)} required />
              </FieldHelp>
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button className="primary-button" type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Onboarding…' : 'Onboard'}
              </button>
              <button className="ghost-button" type="button" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {editClient && canEdit ? (
        <Modal title="Edit client" onClose={() => setEditClient(null)} closeOnBackdrop={false}>
          <p className="modal-card__copy">
            Profile fields that already have a value are locked (owned by the client). Empty fields
            can still be filled here.
          </p>
          <form className="login-form" onSubmit={handleEdit}>
            <FieldHelpGroup>
              <FieldHelp label="Display name" help={clientsHelp.displayName}>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
              </FieldHelp>
              <FieldHelp label="Status" help={clientsHelp.status}>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                </select>
              </FieldHelp>
              <FieldHelp label="Address" help={clientsHelp.address}>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={!isBlank(editClient.address)}
                />
              </FieldHelp>
              <FieldHelp label="NIT" help={clientsHelp.nit}>
                <input
                  value={nit}
                  onChange={(e) => setNit(e.target.value)}
                  disabled={!isBlank(editClient.nit)}
                />
              </FieldHelp>
              <FieldHelp label="City" help={clientsHelp.city}>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={!isBlank(editClient.city)}
                />
              </FieldHelp>
              <FieldHelp label="Country" help={clientsHelp.country}>
                <input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={!isBlank(editClient.country)}
                />
              </FieldHelp>
              <FieldHelp label="Master user ID" help={clientsHelp.masterId}>
                {masterIdLocked ? (
                  <div className="panel-toolbar__actions" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={() => {
                        const id = masterId.trim();
                        if (id) userInfoMutation.mutate(id);
                      }}
                      disabled={userInfoMutation.isPending || !masterId.trim()}
                    >
                      <code>{masterId.trim() || 'usr_…'}</code>
                    </button>
                  </div>
                ) : (
                  <div
                    className="panel-toolbar__actions"
                    style={{ marginTop: '0.5rem', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <input
                      value={masterId}
                      onChange={(e) => setMasterId(e.target.value)}
                      placeholder="usr_…"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                    >
                      {syncMutation.isPending ? 'Syncing…' : 'Sync'}
                    </button>
                  </div>
                )}
              </FieldHelp>
              {!masterIdLocked ? (
                <p className="hint-text">
                  Sync looks up the Core user with role <code>role_&#123;client_id&#125;_master</code>.
                </p>
              ) : (
                <p className="hint-text">Click the ID to view Core user details.</p>
              )}
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button className="primary-button" type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              {editClient.client_type === 'partner' ? (
                <button
                  className="ghost-button"
                  type="button"
                  disabled={passwordResetMutation.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Send a staff-enabled password reset email to this partner master?',
                      )
                    ) {
                      passwordResetMutation.mutate();
                    }
                  }}
                >
                  {passwordResetMutation.isPending ? 'Sending…' : 'Enable password reset'}
                </button>
              ) : null}
              <button className="ghost-button" type="button" onClick={() => setEditClient(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {userInfo ? (
        <Modal title="Master user" onClose={() => setUserInfo(null)}>
          <div className="admin-grid">
            <div className="stat-card">
              <p className="stat-card__label">User ID</p>
              <p className="stat-card__value">
                <code>{userInfo.user_id}</code>
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Cognito username</p>
              <p className="stat-card__value">{userInfo.cognito_username || '—'}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Role</p>
              <p className="stat-card__value">
                <code>{userInfo.role || '—'}</code>
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Status</p>
              <p className="stat-card__value">{userInfo.status || '—'}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Alias</p>
              <p className="stat-card__value">{userInfo.alias?.trim() ? userInfo.alias : '—'}</p>
            </div>
            <div className="stat-card">
              <p className="stat-card__label">Client ID</p>
              <p className="stat-card__value">
                <code>{userInfo.client_id || '—'}</code>
              </p>
            </div>
          </div>
          <div className="modal-card__actions">
            <button className="ghost-button" type="button" onClick={() => setUserInfo(null)}>
              Close
            </button>
          </div>
        </Modal>
      ) : null}
    </AdminShell>
  );
}
